import { useCallback } from 'react';
import { useQueryClient } from 'react-query';

/**
 * Custom hook to manage task mutations (mark as done, remove task)
 * 
 * @param {Object} params - Parameters for the hook
 * @param {string} params.projectId - The project ID
 * @param {Object} params.project - The project data
 * @param {Function} params.toggleTaskStatus - Function to toggle task status
 * @param {Function} params.updateProject - Function to update project
 * @param {Function} params.getProject - Function to get project
 * @param {Function} params.setLocalProject - Function to set local project
 * @param {Function} params.showNotification - Function to show notifications
 * @param {Function} params.refetchSections - Function to refetch sections
 * @returns {Object} Task mutations hook results
 */
const useTaskMutations = ({ 
  projectId, 
  project, 
  toggleTaskStatus, 
  updateProject, 
  getProject,
  setLocalProject,
  showNotification,
  refetchSections
}) => {
  const queryClient = useQueryClient();

  // Handler to mark a topic as completed using the API
  const markTaskAsDone = useCallback(async (tile) => {
    // Make sure we have a valid tile and project ID
    if (!tile || !tile.id) {
      showNotification({
        message: 'Cannot mark topic as done: Missing topic information',
        type: 'error'
      });
      return;
    }
    
    // Validate projectId is not undefined or null
    const currentProjectId = projectId || project?._id;
    if (!currentProjectId) {
      console.error('Project ID is undefined or null');
      showNotification({
        message: 'Cannot mark topic as done: Project ID is missing',
        type: 'error'
      });
      return;
    }
    
    // Get a copy of the current tile for later reference
    const tileCopy = {...tile};
    
    // Immediately update local state to show the checkmark
    // This provides instant visual feedback before the API call completes
    setLocalProject(prevProject => {
      if (!prevProject) return prevProject;
      
      // Create a new completedTasks array with the current task added
      const updatedCompletedTasks = [...(prevProject.completedTasks || [])];
      if (!updatedCompletedTasks.includes(tile.id)) {
        updatedCompletedTasks.push(tile.id);
      }
      
      return {
        ...prevProject,
        completedTasks: updatedCompletedTasks
      };
    });
    
    try {
      // Log project ID to verify it's correct before sending to the API
      console.log('Toggling task status for project ID:', currentProjectId, 'Task:', tile.id);
      
      // Show loading notification
      showNotification({
        message: `Updating status for ${tile.title}...`,
        type: 'info'
      });
      
      // Call the API to toggle task status
      const result = await toggleTaskStatus({
        projectId: currentProjectId,
        taskId: tile.id
      });
      
      // Update local state with the updated project from the API
      setLocalProject(result.project);
      
      // Force refetch sections to update the available tasks
      refetchSections();
      
      // Dispatch event to notify other components that tasks have changed
      window.dispatchEvent(new CustomEvent('taskSelectionCompleted'));
      
      // Invalidate project cache to ensure fresh data
      queryClient.invalidateQueries(['project', currentProjectId]);
      
      // Show appropriate success notification based on result
      showNotification({
        message: result.isCompleted 
          ? `${tileCopy.title} marked as completed` 
          : `${tileCopy.title} marked as pending`,
        type: 'success'
      });
      
      // Log the updated state for debugging
      console.log('Task status updated successfully:', result);
    } catch (error) {
      console.error('Error toggling task status:', error);
      
      // Revert the local state change if API call fails
      // This ensures UI stays in sync with the actual data
      setLocalProject(prevProject => {
        if (!prevProject) return prevProject;
        
        // Create a new completedTasks array without the current task
        const revertedCompletedTasks = (prevProject.completedTasks || [])
          .filter(id => id !== tile.id);
        
        return {
          ...prevProject,
          completedTasks: revertedCompletedTasks
        };
      });
      
      showNotification({
        message: `Failed to update task status: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    }
  }, [projectId, project, toggleTaskStatus, showNotification, setLocalProject, refetchSections, queryClient]);

  // Handler for removing a topic from the project
  const removeTask = useCallback(async (tile) => {
    // Make sure we have a valid tile and project ID
    if (!tile || !tile.id) {
      showNotification({
        message: 'Cannot remove topic: Missing topic information',
        type: 'error'
      });
      return;
    }
    
    // Validate projectId is not undefined or null
    const currentProjectId = projectId || project?._id;
    if (!currentProjectId) {
      console.error('Project ID is undefined or null');
      showNotification({
        message: 'Cannot remove topic: Project ID is missing',
        type: 'error'
      });
      return;
    }
    
    try {
      // Log project ID to verify it's correct before sending to the API
      console.log('Removing topic from project with ID:', currentProjectId);
      
      // Show loading notification
      showNotification({
        message: `Removing topic: ${tile.title}...`,
        type: 'info'
      });
      
      // Get current project to ensure we have latest data
      const currentProject = await getProject(currentProjectId);
      
      if (!currentProject || !currentProject.tasks) {
        throw new Error('Could not retrieve current project data');
      }
      
      // Filter out the task ID to remove
      const updatedTasks = currentProject.tasks.filter(taskId => taskId !== tile.id);
      
      // Log before making the API call
      console.log('Updating project tasks:', {
        projectId: currentProjectId,
        originalTasks: currentProject.tasks,
        updatedTasks: updatedTasks,
        removingTaskId: tile.id
      });
      
      // Make API call to update project
      // updateProject expects an object with projectId and projectData
      const updatedProject = await updateProject({
        projectId: currentProjectId,
        projectData: { tasks: updatedTasks }
      });
      
      if (updatedProject) {
        // Update local state with the updated project
        setLocalProject(updatedProject);
        
        // Also invalidate the query cache to ensure fresh data on next fetch
        queryClient.invalidateQueries(['project', currentProjectId]);
        
        // Force refetch sections to update the available tasks
        refetchSections();
        
        // Dispatch event to notify other components that tasks have changed
        window.dispatchEvent(new CustomEvent('taskSelectionCompleted'));
        
        // Show success notification
        showNotification({
          message: `${tile.title} removed successfully`,
          type: 'success'
        });
        
        // Log the updated state for debugging
        console.log('Project updated successfully:', updatedProject);
      }
    } catch (error) {
      console.error('Error removing topic:', error);
      showNotification({
        message: `Failed to remove topic: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    }
  }, [projectId, project, getProject, updateProject, showNotification, setLocalProject, queryClient, refetchSections]);

  return {
    markTaskAsDone,
    removeTask
  };
};

export default useTaskMutations;